import { ChoiceGroup, DatePicker, DefaultButton, Dialog, DialogFooter, Dropdown, IDropdownOption, MessageBar, MessageBarType, Panel, PanelType, PrimaryButton, Spinner, Stack, TextField, Toggle } from "office-ui-fabric-react";
import * as React from "react";
import { cancelBooking, saveNewBooking } from "../../../api/bookings";
import { getRoomInfo } from "../../../api/rooms";
import { ProfileContext } from "../../../Home";
import { ConstraintType, IBooking } from "../../../types/Booking.types";
import { IRoom } from "../../../types/SiteLocation.types";

export interface IRoomBookingFormProps {
    readOnly?: boolean;
    roomId: number;
    start: Date;
    finish: Date;
    booking?: IBooking;
    roomInfo?: string;
    onDismiss: (changesMade?: boolean) => void;
}

const getStartWeekday = (date: Date): string => {
    return date.toLocaleDateString('en-US', { weekday: 'long' });
};

const getMonthlyNthWeek = (date: Date): number => {
    return Math.min(5, Math.ceil(date.getDate() / 7));
};

const getNextMonthClampedDate = (date: Date): Date => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();

    const nextMonthStart = new Date(year, month + 1, 1);
    const lastDayOfNextMonth = new Date(year, month + 2, 0).getDate();
    const clampedDay = Math.min(day, lastDayOfNextMonth);

    return new Date(nextMonthStart.getFullYear(), nextMonthStart.getMonth(), clampedDay);
};

const getDefaultRecurrenceEndDate = (start: Date, patternType: string): Date => {
    const next = new Date(start);

    if (patternType === "Daily") {
        next.setDate(next.getDate() + 1);
        return next;
    }

    if (patternType === "Weekly") {
        next.setDate(next.getDate() + 7);
        return next;
    }

    return getNextMonthClampedDate(start);
};

const getSeriesEndDateForPayload = (selectedEndDate: Date, bookingStart: Date): Date => {
    return new Date(
        selectedEndDate.getFullYear(),
        selectedEndDate.getMonth(),
        selectedEndDate.getDate(),
        bookingStart.getHours(),
        bookingStart.getMinutes(),
        bookingStart.getSeconds(),
        bookingStart.getMilliseconds()
    );
};

const toDateOnly = (date: Date): Date => {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

const validateEmail = (str?: string) => {
    // eslint-disable-next-line no-control-regex
    const regex = /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/;
    return str && !regex.test(str) ? 'Please enter a valid email' : undefined;
};

export const RoomBookingForm = (props: IRoomBookingFormProps) => {
    const userContext = React.useContext(ProfileContext);

    const [ roomInfo, setRoomInfo ] = React.useState<IRoom>();
    const [ isOwner, setIsOwner ] = React.useState(false);
    const [ newBooking, setNewBooking ] = React.useState(false);
    const [ loading, setLoading ] = React.useState(false);
    const [ saving, setSaving ] = React.useState(false);
    const [ error, setError ] = React.useState<string>();
    const [ showReasonDialog, setShowReasonDialog ] = React.useState(false);
    const [ reason, setReason ] = React.useState<string>();

    const [ onBehalfOf, setOnBehalfOf ] = React.useState<string|undefined>(props.booking?.OnBehalfOfEmail);
    const [ comments, setComments ] = React.useState<string|undefined>(props.booking?.Description);

    const [ recurringEnabled, setRecurringEnabled ] = React.useState(false);
    const [ recurrencePatternType, setRecurrencePatternType ] = React.useState("Daily");
    const [ recurrenceIntervalValue, setRecurrenceIntervalValue ] = React.useState(1);
    const [ recurrenceEndDate, setRecurrenceEndDate ] = React.useState<Date | undefined>(getDefaultRecurrenceEndDate(props.start, "Daily"));
    const [ recurrenceWeeklyDay, setRecurrenceWeeklyDay ] = React.useState<string | undefined>(getStartWeekday(props.start));
    const [ monthlyMode, setMonthlyMode ] = React.useState("DayOfMonth");
    const [ monthlyDayOfMonth, setMonthlyDayOfMonth ] = React.useState<number>(props.start.getDate());
    const [ monthlyNthWeek, setMonthlyNthWeek ] = React.useState<number>(getMonthlyNthWeek(props.start));
    const [ monthlyWeekday, setMonthlyWeekday ] = React.useState<string>(getStartWeekday(props.start));
    const [ recurrenceEndDateUserEdited, setRecurrenceEndDateUserEdited ] = React.useState(false);

    const weekdayOptions: IDropdownOption[] = React.useMemo(() => [
        { key: "Monday", text: "Monday" },
        { key: "Tuesday", text: "Tuesday" },
        { key: "Wednesday", text: "Wednesday" },
        { key: "Thursday", text: "Thursday" },
        { key: "Friday", text: "Friday" },
        { key: "Saturday", text: "Saturday" },
        { key: "Sunday", text: "Sunday" }
    ], []);

    const patternOptions: IDropdownOption[] = React.useMemo(() => [
        { key: "Daily", text: "Daily" },
        { key: "Weekly", text: "Weekly" },
        { key: "Monthly", text: "Monthly" }
    ], []);

    const recurrenceFeatureEnabled = userContext?.Settings?.RecurringRoomBookingsEnabled === true;
    const systemAdmin = userContext?.Profile?.Roles?.some((r) => r.Name === "Admin") === true;
    const privilegedUser = userContext?.Profile?.Roles?.some((r) => r.Name === "Admin") === true || roomInfo?.IsRoomAdmin === true;

    const recurrenceMaxDate: Date | undefined = React.useMemo(() => {
        if (privilegedUser) return undefined;
        const constraint = roomInfo?.RoomBookingConstraint;
        if (!constraint || !constraint.Number || !constraint.Type) return undefined;
        const maxDate = toDateOnly(new Date());
        switch (constraint.Type as ConstraintType) {
            case ConstraintType.Week:
                maxDate.setDate(maxDate.getDate() + (7 * constraint.Number));
                break;
            case ConstraintType.Month:
                maxDate.setMonth(maxDate.getMonth() + constraint.Number);
                break;
            case ConstraintType.Year:
                maxDate.setFullYear(maxDate.getFullYear() + constraint.Number);
                break;
            default:
                return undefined;
        }
        return toDateOnly(maxDate);
    }, [roomInfo, privilegedUser]);

    const clampRecurrenceEndDate = React.useCallback((candidate?: Date) => {
        if (!candidate) return undefined;
        const candidateDate = toDateOnly(candidate);
        if (!recurrenceMaxDate) return candidateDate;

        const maxDate = toDateOnly(recurrenceMaxDate);
        return candidateDate > maxDate ? maxDate : candidateDate;
    }, [recurrenceMaxDate]);

    const monthlyNumericDayConflict = React.useMemo(() => {
        if (!recurringEnabled || recurrencePatternType !== "Monthly" || monthlyMode !== "DayOfMonth" || !recurrenceEndDate) return false;
        let iter = new Date(props.start.getFullYear(), props.start.getMonth(), 1);
        const end = new Date(recurrenceEndDate.getFullYear(), recurrenceEndDate.getMonth(), recurrenceEndDate.getDate());
        while (iter <= end) {
            const daysInMonth = new Date(iter.getFullYear(), iter.getMonth() + 1, 0).getDate();
            if (monthlyDayOfMonth > daysInMonth) return true;
            iter.setMonth(iter.getMonth() + 1);
        }
        return false;
    }, [recurringEnabled, recurrencePatternType, monthlyMode, recurrenceEndDate, props.start, monthlyDayOfMonth]);

    const recurrenceValidationError = React.useMemo(() => {
        if (!recurringEnabled) return undefined;
        if (!recurrenceEndDate) return "Please select a recurrence end date.";
        if (recurrenceMaxDate && toDateOnly(recurrenceEndDate) > toDateOnly(recurrenceMaxDate)) return `Recurrence end date exceeds booking limits. Latest allowed date is ${recurrenceMaxDate.toLocaleDateString()}.`;
        if (recurrencePatternType === "Weekly" && !recurrenceWeeklyDay) return "Please select a weekly recurrence day.";
        if (recurrencePatternType === "Monthly" && monthlyMode === "DayOfMonth" && (monthlyDayOfMonth < 1 || monthlyDayOfMonth > 31)) return "Monthly day must be between 1 and 31.";
        if (monthlyNumericDayConflict) return `Selected monthly day (${monthlyDayOfMonth}) is invalid for one or more months in this series range.`;
        return undefined;
    }, [recurringEnabled, recurrenceEndDate, recurrenceMaxDate, recurrencePatternType, recurrenceWeeklyDay, monthlyMode, monthlyDayOfMonth, monthlyNumericDayConflict]);

    // Cancellation Callback
    const onCancelClick = React.useCallback(async () => {
        let cancel = false, cancelledByAdmin = false;
        if (!isOwner && (roomInfo?.IsRoomAdmin || systemAdmin)) {
            if (!showReasonDialog && !Boolean(reason)) {
                setShowReasonDialog(true);
            } else {
                cancel = true;
                cancelledByAdmin = true;
            }
        } else {
            cancel = window.confirm('Are you sure?');
        }
        if (props.booking && cancel) {
            const response = await cancelBooking({...props.booking, CancellationReason: reason, CancelledByAdmin: cancelledByAdmin });
            if (response.ok) {
                props.onDismiss(true);
            } else {
                setError("Unable to cancel booking");
            }
        }
    }, [ props, isOwner, reason, roomInfo, showReasonDialog, systemAdmin ]);

    // Save Callback
    const save = React.useCallback(async () => {
        setSaving(true);
        const booking: Partial<IBooking> = {};
        if (userContext) booking.BookedByUserId = userContext.Profile.Id;
        booking.Description = comments;
        booking.Start = props.start.toISOString();
        booking.Finish = props.finish.toISOString();
        if (props.booking && props.booking.Id) booking.Id = props.booking.Id;
        booking.OnBehalfOfEmail = onBehalfOf;
        booking.RoomId = props.roomId;
        if (recurringEnabled) {
            booking.IsRecurring = true;
            booking.RecurrencePatternType = recurrencePatternType;
            booking.RecurrenceIntervalValue = recurrenceIntervalValue;
            booking.SeriesStartDate = props.start.toISOString();
            booking.SeriesEndDate = recurrenceEndDate
                ? getSeriesEndDateForPayload(recurrenceEndDate, props.start).toISOString()
                : undefined;
            if (recurrencePatternType === "Weekly") {
                booking.RecurrenceDayOfWeek = recurrenceWeeklyDay;
            }
            if (recurrencePatternType === "Monthly") {
                booking.MonthlyMode = monthlyMode;
                if (monthlyMode === "DayOfMonth") {
                    booking.RecurrenceDayOfMonth = monthlyDayOfMonth.toString();
                }
                if (monthlyMode === "NthWeekday") {
                    booking.MonthlyNthWeek = monthlyNthWeek;
                    booking.MonthlyWeekday = monthlyWeekday;
                }
                if (monthlyMode === "LastWeekday") {
                    booking.MonthlyWeekday = monthlyWeekday;
                }
            }
        }

        const response = await saveNewBooking(recurringEnabled ? "room/series" : "room", booking as IBooking);
        if (response.ok) {
            props.onDismiss(true);
        } else {
            setSaving(false);
            const responseTxt = await response.text();
            setError(responseTxt || "Unable to save booking");
        }
    }, [ props, onBehalfOf, comments, userContext, recurringEnabled, recurrencePatternType, recurrenceIntervalValue, recurrenceEndDate, recurrenceWeeklyDay, monthlyMode, monthlyDayOfMonth, monthlyNthWeek, monthlyWeekday ]);

    // Callback to check if user is a room admin
    const checkIsAdmin = React.useCallback(async () => {
        setLoading(true);
        const response = await getRoomInfo(props.roomId);
        if (response.ok) {
            setRoomInfo(await response.json() as IRoom);
        } else {
            setError("Unable to load room information");
        }
        setLoading(false);
    }, [ props.roomId ]);

    // Load room info when the target room changes.
    React.useEffect(() => {
        checkIsAdmin();
    }, [checkIsAdmin]);

    // Initialize ownership/new-booking state from props/user context.
    React.useEffect(() => {
        if (userContext && props.booking && (
            props.booking.BookedByUserId === userContext.Profile.Id ||
            (props.booking.OnBehalfOfEmail && userContext.Profile.Email && props.booking.OnBehalfOfEmail.toLowerCase() === userContext.Profile.Email.toLowerCase())
        )) {
            setIsOwner(true);
            return;
        }

        setIsOwner(false);
        if (!props.booking) {
            setNewBooking(true);
            setOnBehalfOf(userContext?.Profile?.Email);
            setRecurrencePatternType("Daily");
            setRecurrenceEndDate(clampRecurrenceEndDate(getDefaultRecurrenceEndDate(props.start, "Daily")));
            setRecurrenceEndDateUserEdited(false);
            setRecurrenceWeeklyDay(getStartWeekday(props.start));
            setMonthlyDayOfMonth(props.start.getDate());
            setMonthlyNthWeek(getMonthlyNthWeek(props.start));
            setMonthlyWeekday(getStartWeekday(props.start));
        } else {
            setNewBooking(false);
        }
    }, [props.booking, props.start, userContext, clampRecurrenceEndDate]);

    React.useEffect(() => {
        if (!newBooking || !recurringEnabled || recurrenceEndDateUserEdited) {
            return;
        }

        setRecurrenceEndDate(clampRecurrenceEndDate(getDefaultRecurrenceEndDate(props.start, recurrencePatternType)));
    }, [newBooking, recurringEnabled, recurrencePatternType, recurrenceEndDateUserEdited, props.start, clampRecurrenceEndDate]);

    React.useEffect(() => {
        if (!newBooking || !recurringEnabled) {
            return;
        }

        setRecurrenceEndDate((current) => clampRecurrenceEndDate(current));
    }, [newBooking, recurringEnabled, clampRecurrenceEndDate]);

    let title: string = "";
    if (newBooking) title = roomInfo?.IsRestricted && roomInfo?.IsRoomAdmin === false ? "Request Room" : "New Booking";
    if (props.booking) title = roomInfo?.IsRoomAdmin === true || isOwner ? "Edit Booking" : "View Booking";

    return (
        <Panel 
        isOpen={true} 
        type={PanelType.medium} 
        headerText={title}
        onDismiss={() => props.onDismiss()}
        isFooterAtBottom={true}
        onRenderFooter={() => (
            <Stack styles={{root: {padding: 20}}} horizontal horizontalAlign="space-between" verticalAlign="center" tokens={{ childrenGap: 10}}>
                <div>
                    { !newBooking && !props.readOnly && (isOwner || roomInfo?.IsRoomAdmin || systemAdmin) && (
                        <DefaultButton text="Cancel" iconProps={{iconName: "Delete"}} onClick={onCancelClick} />
                    )}
                </div>
                <div>
                    { newBooking && (roomInfo?.IsRestricted === false || (roomInfo?.IsRestricted && roomInfo?.IsRoomAdmin)) && (
                        <PrimaryButton disabled={saving || (!Boolean(comments) || comments?.trim() === '') || Boolean(recurrenceValidationError)} iconProps={{ iconName: "Save" }} onClick={save}>
                            {saving === true && <><Spinner />&emsp;</>}
                            Save
                        </PrimaryButton>
                    )}
                    { newBooking && roomInfo?.IsRestricted && roomInfo?.IsRoomAdmin === false && (
                        <PrimaryButton disabled={saving || (!Boolean(comments) || comments?.trim() === '') || Boolean(recurrenceValidationError)} iconProps={{ iconName: "Subscribe" }} onClick={save}>
                            {saving === true && <><Spinner />&emsp;</>}
                            Request
                        </PrimaryButton>
                    )}
                </div>
            </Stack>
        )}
        >
            { roomInfo !== undefined && (
                <MessageBar styles={{ innerText: { whiteSpace: "pre-wrap" } }} messageBarType={MessageBarType.warning}>
                    <strong>Room:</strong> {roomInfo.Name}&emsp;
                    <strong>Location:</strong> {roomInfo.LocationName}&emsp;
                    <strong>Site:</strong> {roomInfo.SiteName}
                    {roomInfo.RoomCheckIns === true && (
                        <div>
                            <p><strong>Please Note:</strong> This room requires check-in on arrival.</p>
                            <p>
                                You will be able to check-in during the first 15 minutes of the meeting, and 15 minutes before.
                                Failure to check-in will mean that the booking is automatically cancelled and the resource made available for others.
                            </p>
                        </div>
                    )}
                </MessageBar>
            )}
            { error !== undefined && (
                <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>
            )}
            { recurrenceValidationError !== undefined && (
                <MessageBar messageBarType={MessageBarType.error}>{recurrenceValidationError}</MessageBar>
            )}
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
                <TextField label="From" disabled value={`${props.start.toLocaleDateString()} ${props.start.toLocaleTimeString()}`} />
                <TextField label="To" disabled value={`${props.finish.toLocaleDateString()} ${props.finish.toLocaleTimeString()}`} />
            </Stack>
            <TextField label="On Behalf Of"
                placeholder="Enter email here"
                disabled={!newBooking}
                value={onBehalfOf}
                onChange={(e, newValue) => {
                    setOnBehalfOf(newValue);
                }} 
                errorMessage={validateEmail(onBehalfOf)}
            />
            <TextField 
                autoFocus
                label="Team Name"
                rows={10}
                disabled={props.readOnly || !newBooking}
                value={comments}
                onChange={(e, newValue) => setComments(newValue)}
                errorMessage={newBooking && (!Boolean(comments) || comments?.trim() === '') ? 'Please provide a meeting name or reason for booking.' : undefined}
                multiline
            />
            { newBooking && recurrenceFeatureEnabled && (
                <>
                    <Toggle
                        label="Recurring Booking"
                        checked={recurringEnabled}
                        onText="On"
                        offText="Off"
                        onChange={(e, checked) => {
                            const enabled = checked === true;
                            setRecurringEnabled(enabled);
                            if (enabled) {
                                setRecurrenceEndDateUserEdited(false);
                                setRecurrenceEndDate(clampRecurrenceEndDate(getDefaultRecurrenceEndDate(props.start, recurrencePatternType)));
                                setRecurrenceWeeklyDay(getStartWeekday(props.start));
                                setMonthlyDayOfMonth(props.start.getDate());
                                setMonthlyNthWeek(getMonthlyNthWeek(props.start));
                                setMonthlyWeekday(getStartWeekday(props.start));
                            }
                        }}
                        disabled={props.readOnly}
                    />
                    { recurringEnabled && (
                        <Stack tokens={{ childrenGap: 10 }}>
                            <Dropdown
                                label="Pattern"
                                selectedKey={recurrencePatternType}
                                options={patternOptions}
                                onChange={(e, opt) => setRecurrencePatternType(opt?.key as string)}
                            />
                            <TextField
                                label={`Repeat every ${recurrencePatternType === "Daily" ? "day(s)" : recurrencePatternType === "Weekly" ? "week(s)" : "month(s)"}`}
                                type="number"
                                min={1}
                                value={recurrenceIntervalValue.toString()}
                                onChange={(e, val) => {
                                    const parsed = parseInt(val || "1", 10);
                                    setRecurrenceIntervalValue(!isNaN(parsed) && parsed > 0 ? parsed : 1);
                                }}
                            />
                            <DatePicker
                                label="Recurrence End Date"
                                value={recurrenceEndDate}
                                minDate={new Date()}
                                maxDate={recurrenceMaxDate}
                                onSelectDate={(d) => {
                                    setRecurrenceEndDate(clampRecurrenceEndDate(d || undefined));
                                    setRecurrenceEndDateUserEdited(true);
                                }}
                            />
                            { recurrencePatternType === "Weekly" && (
                                <Dropdown
                                    label="Weekday"
                                    selectedKey={recurrenceWeeklyDay}
                                    options={weekdayOptions}
                                    onChange={(e, opt) => setRecurrenceWeeklyDay(opt?.key as string)}
                                />
                            )}
                            { recurrencePatternType === "Monthly" && (
                                <>
                                    <ChoiceGroup
                                        label="Monthly Options"
                                        selectedKey={monthlyMode}
                                        options={[
                                            { key: "DayOfMonth", text: `On day ${monthlyDayOfMonth}` },
                                            { key: "NthWeekday", text: `On the ${monthlyNthWeek}${monthlyNthWeek === 1 ? "st" : monthlyNthWeek === 2 ? "nd" : monthlyNthWeek === 3 ? "rd" : "th"} ${monthlyWeekday}` },
                                            { key: "LastWeekday", text: `On the last ${monthlyWeekday}` }
                                        ]}
                                        onChange={(e, opt) => setMonthlyMode(opt?.key || "DayOfMonth")}
                                    />
                                    { monthlyMode === "DayOfMonth" && (
                                        <TextField
                                            label="Day of Month (1-31)"
                                            type="number"
                                            min={1}
                                            max={31}
                                            value={monthlyDayOfMonth.toString()}
                                            onChange={(e, val) => {
                                                const parsed = parseInt(val || "1", 10);
                                                setMonthlyDayOfMonth(!isNaN(parsed) ? parsed : 1);
                                            }}
                                        />
                                    )}
                                    { monthlyMode === "NthWeekday" && (
                                        <Stack horizontal tokens={{ childrenGap: 10 }}>
                                            <Dropdown
                                                label="Week of Month"
                                                selectedKey={monthlyNthWeek}
                                                options={[1,2,3,4,5].map((n) => ({ key: n, text: `${n}` }))}
                                                onChange={(e, opt) => setMonthlyNthWeek(opt?.key as number)}
                                            />
                                            <Dropdown
                                                label="Weekday"
                                                selectedKey={monthlyWeekday}
                                                options={weekdayOptions}
                                                onChange={(e, opt) => setMonthlyWeekday(opt?.key as string)}
                                            />
                                        </Stack>
                                    )}
                                    { monthlyMode === "LastWeekday" && (
                                        <Dropdown
                                            label="Weekday"
                                            selectedKey={monthlyWeekday}
                                            options={weekdayOptions}
                                            onChange={(e, opt) => setMonthlyWeekday(opt?.key as string)}
                                        />
                                    )}
                                </>
                            )}
                        </Stack>
                    )}
                </>
            )}
            { showReasonDialog && (
                <Dialog 
                    hidden={false}
                    dialogContentProps={{ showCloseButton: true, title: "Cancel Booking", subText: "Please provide a reason for cancelling this booking:" }} 
                    onDismiss={() => {
                        setReason(undefined);
                        setShowReasonDialog(false);
                    }}>
                    <TextField autoFocus multiline label="Cancellation Reason" placeholder="Please enter a cancellation reason" value={reason} onChange={(e, newVal) => setReason(newVal)} />
                    <DialogFooter >
                        <Stack horizontal horizontalAlign="end" verticalAlign="center" tokens={{childrenGap: 10}}>
                            <PrimaryButton disabled={!Boolean(reason)} text="Cancel Booking" onClick={onCancelClick} />
                            <DefaultButton text="Keep Booking" onClick={() => {
                                setReason(undefined);
                                setShowReasonDialog(false);
                            }} />
                        </Stack>
                    </DialogFooter>
                </Dialog>
            )}
            { loading === true && <Spinner />}
            {/* { roomInfo?.IsRoomAdmin === true && (<>Is Admin</>)}
            { isOwner === true && (<>Is Owner</>)}
            { newBooking === true && (<>New Booking</>)} */}
        </Panel>
    );
}